import React, { useMemo } from 'react';
import SvgIcon from '../../../../utils/SvgIcon';
import { getStatusClass, capitalizedValue } from '../../../../PortalListing/utils/tableCellUtils';
import {
  formatDate,
  formatDateWithTime,
  formatShortDate,
  formatNextScheduledDate,
  formatTime,
} from '../../../../utils/DateHelpers';
import { useJobsConfig } from '../../../../PortalJobListing/hooks/useJobsConfig';

type JobStatus = 'unallocated' | 'complete' | 'rejected';

type FocusJob = {
  serviceType: string;
  serviceName: string;
  containerType: string;
  jobDate: string;
  status: string;
  statusMessage: string | null;
  url: string;
  nextScheduledDate: string | null;
};

type NextJobSummaryOneServiceProps = {
  focusJob: FocusJob;
  monthlySummary?: {
    completedTotal?: number;
    completedUrl?: string;
  };
};

const mapStatusToJobStatus = (status: string): JobStatus => {
  const value = status.toLowerCase();
  if (value === 'complete' || value === 'completed') return 'complete';
  if (value === 'rejected') return 'rejected';
  return 'unallocated';
};

const NextJobSummaryOneService = ({ focusJob, monthlySummary }: NextJobSummaryOneServiceProps) => {
  const { labels } = useJobsConfig();

  const status = useMemo(() => mapStatusToJobStatus(focusJob.status), [focusJob.status]);

  const showNextCollection = status === 'complete' || status === 'rejected';

  const title = useMemo(() => {
    switch (status) {
      case 'complete':
        return labels.titleCompleted;
      case 'rejected':
        return labels.titleRejected;
      default:
        return labels.titleUnallocated;
    }
  }, [status, labels.titleCompleted, labels.titleRejected, labels.titleUnallocated]);

  const formattedDate = useMemo(
    () => (showNextCollection ? formatDateWithTime(focusJob.jobDate) : formatDate(focusJob.jobDate)),
    [showNextCollection, focusJob.jobDate]
  );

  const statusDate = useMemo(
    () => (showNextCollection ? formatTime(focusJob.jobDate) : formatShortDate(focusJob.jobDate)),
    [showNextCollection, focusJob.jobDate]
  );

  const serviceType = focusJob.serviceName || focusJob.serviceType || '';

  const statusLabel = useMemo(() => capitalizedValue(focusJob.status), [focusJob.status]);

  const nextCollectionSentence =
    focusJob.nextScheduledDate &&
    labels.nextScheduledCollectionSentence.replace('{date}', formatNextScheduledDate(focusJob.nextScheduledDate));

  return (
    <div className="next-job-summary">
      <div className="next-job-summary__body">
        <div className="next-job-summary__header">
          <div className="next-job-summary__icon-container">
            <SvgIcon viewBox="0 0 16 16" iconName="icon-portaltruck" className="next-job-summary__icon" />
          </div>

          <div className="next-job-summary__header-content">
            <div className="next-job-summary__title">{title}</div>
            <div className="next-job-summary__date">{formattedDate}</div>
          </div>
        </div>

        <div className={`next-job-summary__content-card next-job-summary__content-card--${status}`}>
          <div className="next-job-summary__content-card-top">
            <div className="next-job-summary__waste-info">
              <div className="next-job-summary__waste-type">{serviceType}</div>
              {focusJob.containerType && <div className="next-job-summary__bin-info">{focusJob.containerType}</div>}
            </div>

            <div className="next-job-summary__status-section">
              <span
                className={`next-job-summary__status-label next-job-summary__status-label--${getStatusClass(
                  focusJob.status
                )}`}
              >
                {statusLabel}
              </span>
              <div className="next-job-summary__status-date">{statusDate}</div>
            </div>
          </div>

          {status === 'rejected' && focusJob.statusMessage && (
            <div className="next-job-summary__rejection-details">
              <div className="next-job-summary__rejection-reason">{focusJob.statusMessage}</div>
              {focusJob.url && (
                <a href={focusJob.url} className="next-job-summary__rejection-link">
                  {labels.rejectedJobDetailsLink}
                </a>
              )}
            </div>
          )}
        </div>

        {showNextCollection && nextCollectionSentence && (
          <div className="next-job-summary__next-collection">
            <div className="next-job-summary__next-collection-icon">
              <svg className="icon" aria-hidden="true">
                <use href="/dist/assets/icons/sprite.svg#refresh-double" />
              </svg>
            </div>
            <div className="next-job-summary__next-collection-text">{nextCollectionSentence}</div>
          </div>
        )}

        <div className="next-job-summary__action">
          <div className="next-job-summary__action-question">{labels.requestCollectionQuestion}</div>
          {focusJob.url && (
            <a href={focusJob.url} className="next-job-summary__action-link">
              {labels.requestCollectionCta}
            </a>
          )}
        </div>
      </div>

      {monthlySummary?.completedUrl && (
        <a href={monthlySummary.completedUrl} className="next-job-summary__footer">
          <div className="next-job-summary__footer-text">
            <span className="next-job-summary__footer-text-number">{monthlySummary.completedTotal ?? 0}</span>
            <span className="next-job-summary__footer-text-label">{labels.jobsCompletedMonth}</span>
          </div>
          <div className="next-job-summary__footer-icon">
            <SvgIcon viewBox="0 0 16 16" iconName="arrow-right" className="next-job-summary__footer-icon-icon" />
          </div>
        </a>
      )}
    </div>
  );
};

export default NextJobSummaryOneService;
